import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { SCOPE_LABEL, defaultSheet } from '../datasheet';
import NetSuitePush from './NetSuitePush';
import { fmtDate, fmtDateTime, asDate } from '../timefmt';

const SCOPES = ['core', 'customer', 'personal'];

export default function NetSuiteView({ toast }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState({});
  const [review, setReview] = useState(null);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    const all = [];
    for (const s of SCOPES) { const r = await api.listProducts({ scope: s }); all.push(...r.items.map(p => ({ ...p, scope: s }))); }
    setItems(all);
    try { setStatus(await api.nsStatus()); } catch (e) { console.warn('NetSuite status check failed', e); }
    try { const h = await api.nsPushes(); setHistory(h.items || []); } catch (e) { console.warn('Push history load failed', e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(p => {
    if (!q) return true;
    const t = [p.name, p.code, p.family, (p.data || {}).variant].join(' ').toLowerCase();
    return q.toLowerCase().split(/\s+/).filter(Boolean).every(x => t.includes(x));
  });
  const selected = items.filter(p => sel[p.id]);

  // product_id -> most recent push (history is sorted newest-first)
  const lastPush = {};
  history.forEach(h => { if (h.product_id && !lastPush[h.product_id]) lastPush[h.product_id] = h; });

  function openReview() {
    setReview(selected.map(p => ({
      key: p.id,
      name: p.name,
      productId: p.id,
      sheet: { ...defaultSheet(), ...(p.data || {}), family: p.family || (p.data || {}).family || '' },
    })));
  }

  return (
    <div className="ns-view" data-testid="netsuite-view">
      <div className="ns-view-head">
        <div>
          <h2>NetSuite — bulk push</h2>
          <p className="ns-view-sub">
            Select datasheets, then review exactly what will be inserted into NetSuite for each one.
            Per item you can pick an existing NetSuite item to replace, or create it as a new item.
            {status && (status.configured
              ? <span className="ns-conn ok"> Connected: {status.account_id}</span>
              : <span className="ns-conn bad"> NetSuite not configured</span>)}
          </p>
        </div>
        <div className="ns-view-actions">
          <span data-testid="ns-selected-count">{selected.length} selected</span>
          <button className="btn btn-sm" onClick={() => setSel({})} disabled={!selected.length}>Clear</button>
          <button className="btn btn-primary" data-testid="ns-review-btn" disabled={!selected.length} onClick={openReview}>
            Review &amp; push ({selected.length})
          </button>
        </div>
      </div>

      <input className="input ns-view-search" data-testid="ns-search" placeholder="Search name, code, family…"
        value={q} onChange={e => setQ(e.target.value)} />

      <div className="ns-view-list">
        {filtered.length === 0 && <div className="empty-note">No datasheets found.</div>}
        {filtered.map(p => (
          <label className={'ns-view-row' + (sel[p.id] ? ' on' : '')} key={p.id} data-testid={'ns-row-' + p.id}>
            <input type="checkbox" checked={!!sel[p.id]} onChange={e => setSel(s => ({ ...s, [p.id]: e.target.checked }))} />
            <span className="nm">{p.name || '(Untitled)'}</span>
            {p.code && <span className="cd">{p.code}</span>}
            {p.family && <span className="fam">{p.family}</span>}
            {lastPush[p.id] ? (
              (p.updated_at && lastPush[p.id].ts && asDate(p.updated_at) > asDate(lastPush[p.id].ts)) ? (
                <span className="ns-lastpush stale" data-testid={'ns-lastpush-' + p.id}
                  title={'Datasheet edited after its last push (' + fmtDateTime(lastPush[p.id].ts) + ') — out of sync with NetSuite'}>
                  Edited since push
                </span>
              ) : (
                <span className="ns-lastpush on" data-testid={'ns-lastpush-' + p.id}
                  title={'Last pushed by ' + (lastPush[p.id].user_name || lastPush[p.id].user_email || '?') + (lastPush[p.id].item_name ? ' → ' + lastPush[p.id].item_name : '')}>
                  {lastPush[p.id].mode === 'create' ? 'Created' : 'Merged'} {lastPush[p.id].ts ? fmtDate(lastPush[p.id].ts) : ''}
                </span>
              )
            ) : (
              <span className="ns-lastpush never" data-testid={'ns-lastpush-' + p.id}>Never pushed</span>
            )}
            <span className={'scope-chip scope-' + p.scope}>{SCOPE_LABEL[p.scope]}</span>
          </label>
        ))}
      </div>

      {history.length > 0 && (
        <div className="ns-history" data-testid="ns-push-history">
          <h3>Push history</h3>
          <table className="ns-history-table">
            <thead><tr><th>When</th><th>Who</th><th>Action</th><th>Item</th><th></th></tr></thead>
            <tbody>
              {history.map(h => (
                <tr key={h.id} data-testid={'ns-history-row-' + h.id}>
                  <td>{h.ts ? fmtDateTime(h.ts) : ''}</td>
                  <td>{h.user_name || h.user_email}</td>
                  <td><span className={'ns-hist-mode ' + h.mode}>{h.mode === 'create' ? 'Created' : 'Merged'}</span></td>
                  <td className="cd">{h.item_name || '#' + h.ns_item_id}</td>
                  <td>{h.url && <a href={h.url} target="_blank" rel="noreferrer">open ↗</a>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {review && <NetSuitePush entries={review} onClose={() => { setReview(null); load(); }} toast={toast} />}
    </div>
  );
}
